import { useState, useEffect, useMemo } from 'react'
import B2BApiService from "../../B2bApiService";
import AssessmentProgress, { buildAssessmentNameMap } from '../reusableElementsComponents/AssessmentProgress';
import { Select, Text, TextArea } from "../formComponents/Inputs";
import { TextField } from "@mui/material";
import * as Constants from "../../constants";
import { currentUserRole, validateField, findIndustryBySelectValue } from '../../Utilities'
import { localizeIndustryList, t } from '../../content';
import { useLocale } from '../../locale';
import email from "../../assets/images/icons/email.svg";
import Button from "../reusableElementsComponents/Button";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EmailIcon from '@mui/icons-material/Email';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import { Tooltip } from 'react-tooltip'
import Modal from '../reusableElementsComponents/Modal';
import WriteAccessGuard from '../reusableElementsComponents/WriteAccessGuard';
import { toast } from 'react-toastify';


function TeamReviewGeneralEdit({ data, updateData, teamHash, dataEditedFlag, setDataEditedFlag }) {
    const { locale } = useLocale();

    let [jobIndustries, setJobIndustries] = useState([]);
    const departmentOptions = useMemo(
        () => localizeIndustryList(jobIndustries, locale),
        [jobIndustries, locale]
    );

    const [newMemberData, setNewMemberData] = useState({
        first_name: '',
        last_name: '',
        email: ''
    });
    const [newMemberDataErrors, setNewMemberDataErrors] = useState({
        first_name: false,
        last_name: false,
        email: false
    });
    const newMemberEmailErrorMessages = {
        first_name: t('validation.cannotBeEmpty'),
        last_name: t('validation.cannotBeEmpty'),
        email: t('validation.invalidEmail')
    };
    const [resendingInvite, setResendingInvite] = useState(false)
    const [visible, setVisible] = useState(false);
    const [selectedInviteUrl, setSelectedInviteUrl] = useState("")
    const [allAssessments, setAllAssessments] = useState([]);

    const b2bServiceObj = useMemo(() => {
        return new B2BApiService();
    }, []);

    useEffect(() => {
        b2bServiceObj.LoadIndustriesList(setJobIndustries);
    }, [b2bServiceObj, setJobIndustries]);

    useEffect(() => {
        if (data?.assessments?.length > 0) {
            b2bServiceObj.LoadAssessments(setAllAssessments);
        }
    }, [b2bServiceObj, data?.assessments?.length]);

    const assessmentNames = useMemo(() => buildAssessmentNameMap(allAssessments, locale), [allAssessments, locale]);

    const handleChange = (value, key) => {
        // For industry
        if (key === 'industry') {
            const match = findIndustryBySelectValue(jobIndustries, value);
            if (match) {
                updateData({ ...data, industry: match});
                setDataEditedFlag(true);
            }
            return;
        }
        // For string values, we need to check if the value is actually changed to edit the dataEditedFlag
        if (typeof value === 'string') {
            if (value.trim() !== data[key]?.toString().trim()) {
                setDataEditedFlag(true);
            }
            updateData({ ...data, [key]: value });
            return;
        }
        setDataEditedFlag(true)
        updateData({ ...data, [key]: value });
    }

    const removeMemberFromTeam = (member) => {
        let updatedMemberList = data?.members?.filter(m => m.email.toLowerCase() !== member.email.toLowerCase());
        handleChange(updatedMemberList, 'members');
    }

    const addNewTeamMember = () => {
        const newMemberFirstnameError = newMemberData.first_name.trim() === '';
        const newMemberLastnameError = newMemberData.last_name.trim() === '';
        const newMemberEmailError = !Constants.VALID_EMAIL.test(newMemberData.email);
        setNewMemberDataErrors({
            first_name: newMemberFirstnameError,
            last_name: newMemberLastnameError,
            email: newMemberEmailError
        });
        if (newMemberFirstnameError || newMemberLastnameError || newMemberEmailError) {
            return;
        }
        if (data?.members?.some(m => m.email.toLowerCase() === newMemberData.email.toLowerCase())) {
            toast.error(t('teams.toasts.memberExists'), {containerId: "teamManagementToast"});
            return;
        } else {
            const newMember = {
                email: newMemberData.email,
                first: newMemberData.first_name,
                last: newMemberData.last_name,
                user_status: 10,
                invite_status: 1,
                invited: true,
                invite_accepted: false,
                invite_rejected: false
            };
            let updatedMemberList = [...data?.members, newMember];
            handleChange(updatedMemberList, 'members');
            setNewMemberData({ first_name: '', last_name: '', email: '' });
        }
    };

    const resendInvite = (member) => {
        if (resendingInvite) {
            return;
        }

        setResendingInvite(true)

        let payload = { "user-id": member.id, "team-hash": teamHash };
        b2bServiceObj.ResendTeamInvite(payload, (res) => {
            if(res.status){
                toast.success(t('teams.toasts.inviteResent'), {containerId: "teamManagementToast"});
            }else{
                toast.error(t('teams.toasts.inviteResendError'), {containerId: "teamManagementToast"});
            }
            setResendingInvite(false);
        });
    };

    const copyUrlToClipboard = () =>{
        navigator.clipboard.writeText(selectedInviteUrl);
        toast.success(t('toasts.inviteUrlCopied'), {containerId: "teamManagementToast"});
        setVisible(false);
    }

    return (
        <>
            <div className="title-and-target-profile section">
                <div className="title">
                    <div className="field-label">{t('teams.teamName')} <span className="mandatory">*</span></div>
                    <Text
                        title=''
                        required={true}
                        fullWidth={true}
                        doValidation={!validateField("text", data?.title).isValid}
                        errorContent={t('validation.enterTeamTitle')}
                        handleChange={e => handleChange(e.target.value, 'title')}
                        value={data?.title || ''}
                    />
                </div>
                <div className="department">
                    <div className="field-label">{t('recruitment.form.department')} <span className="mandatory">*</span></div>
                    <Select
                        id={'industry'}
                        label={t('teams.departmentsList')}
                        options={departmentOptions}
                        doValidation={!validateField("item", data?.industry).isValid}
                        errorContent={t('validation.selectJobDepartment')}
                        handleChange={e => handleChange(e.target.value, 'industry')}
                        option={data?.industry?.id ?? ''}
                        defaultValue={t('teams.selectJobDepartment')}
                    />
                </div>
            </div>

            <div className="description">
                <div className="field-label">{t('recruitment.form.jobDescription')}</div>
                <TextArea
                    title=''
                    required={false}
                    fullWidth={true}
                    handleChange={(e) => handleChange(e.target.value, 'description')}
                    value={data?.description || ''}
                />
            </div>

            <div className="team-members-section section">
                <div className="field-label">{t('teams.teamMembersList')} <span className="mandatory">*</span></div>
                {(!data?.members || data?.members?.length === 0) && <div><br /><p className="m-0">{t('emptyStates.noMembers')}</p></div>}
                <ul className='teamMembers'>
                    {data?.members && data?.members?.map((member, index) => {
                        return (
                            <li className="teamMember-list-item"
                                key={index}>
                                <div className={`teamMember-info ${member.invited ? "pending" : ""} ${member.invite_rejected ? "rejected" : ""}`}>
                                    <div className="title">
                                        <p className='name'>{member.first}{" "}{member.last}</p>
                                        <p className='email m-0'>{member.email}</p>
                                    </div>
                                
                                    <div className="actions">
                                        {
                                            member?.invited && (
                                                <WriteAccessGuard nativeDisabled={false}>
                                                    <EmailIcon
                                                        className='icon email button-tooltip-element'
                                                        src={email} alt=""
                                                        data-tooltip-content={t('teams.resendInvite')}
                                                        onClick={() => {
                                                            resendInvite(member)
                                                        }}
                                                    />
                                                </WriteAccessGuard>)
                                        }
                                        {
                                            member?.invite_url && (
                                                <ContentCopyIcon
                                                    className='icon copy-url button-tooltip-element'
                                                    data-tooltip-content={t('teams.copyInviteUrl')}
                                                    onClick={() => {
                                                    setSelectedInviteUrl(member?.invite_url)
                                                    setVisible(true)
                                                }}
                                            />)

                                        }
                                        {![Constants.USER_ROLE_TEAMLEAD, Constants.USER_ROLE_SPECIALIST].includes(currentUserRole()) && <WriteAccessGuard nativeDisabled={false}>
                                            <DeleteForeverIcon
                                                className='icon bin button-tooltip-element'
                                                data-tooltip-content={t('teams.removeMember')}
                                                onClick={() => {
                                                    removeMemberFromTeam(member)
                                                }}
                                            />
                                        </WriteAccessGuard>}
                                    </div>
                                </div>
                                <AssessmentProgress
                                    requiredAssessments={data.assessments || []}
                                    completedAssessments={member.completed_assessments || []}
                                    assessmentNames={assessmentNames}
                                />
                            </li>
                        )
                    })}
                </ul>
                {![Constants.USER_ROLE_TEAMLEAD, Constants.USER_ROLE_SPECIALIST].includes(currentUserRole()) && <div>
                    <h5><strong>{t('teams.inviteNewMember')}</strong></h5>
                    <div className="inviteteamMember">
                    <TextField
                        className="text-field"
                        variant="standard"
                        id="candidate_first_name"
                        label={t('common.fields.firstName')}
                        name="candidate_first_name"
                        type="text"
                        value={newMemberData.first_name || ''}
                        onChange={(e) => {
                            setNewMemberData({
                                ...newMemberData,
                                first_name: e.target.value
                            });
                            setNewMemberDataErrors({
                                ...newMemberDataErrors,
                                first_name: e.target.value.trim() === ''
                            });
                        }}
                        error={newMemberDataErrors.first_name}
                        helperText={newMemberDataErrors.first_name ? newMemberEmailErrorMessages.first_name : ''}
                    />
                    <TextField
                        className="text-field"
                        variant="standard"
                        id="candidate_last_name"
                        label={t('common.fields.lastName')}
                        name="candidate_last_name"
                        type="text"
                        value={newMemberData.last_name || ''}
                        onChange={(e) => {
                            setNewMemberData({
                                ...newMemberData,
                                last_name: e.target.value
                            });
                            setNewMemberDataErrors({
                                ...newMemberDataErrors,
                                last_name: e.target.value.trim() === ''
                            });
                        }}
                        error={newMemberDataErrors.last_name}
                        helperText={newMemberDataErrors.last_name ? newMemberEmailErrorMessages.last_name : ''}
                    />
                    
                    <TextField
                        className="text-field"
                        variant="standard"
                        id="candidate_email"
                        label={t('common.fields.email')}
                        name="candidate_email"
                        type="text"
                        value={newMemberData.email || ''}
                        onChange={(e) => {
                            setNewMemberData({
                                ...newMemberData,
                                email: e.target.value
                            });
                            setNewMemberDataErrors({
                                ...newMemberDataErrors,
                                email: !Constants.VALID_EMAIL.test(e.target.value)
                            });
                        }}
                        error={newMemberDataErrors.email}
                        helperText={newMemberDataErrors.email ? newMemberEmailErrorMessages.email : ''}
                    />
                    <WriteAccessGuard>
                        <Button btnstyle={'mazhrBtn light-green invite-button'} onClick={addNewTeamMember}> <PersonAddAlt1Icon /> &nbsp; {t('common.actions.invite')}</Button>
                    </WriteAccessGuard>
                    </div>
                </div>}
                <Tooltip anchorSelect=".button-tooltip-element" />
                <Modal visible={visible} onHide={()=> setVisible(false)}
                    title={t('teams.inviteUrlTitle')}
                    text={selectedInviteUrl}
                    actionButtons={[{ label: t('common.actions.copy'), onClick: () => copyUrlToClipboard() }]}
                    cancel={()=> setVisible(false)} />
            </div>
            {dataEditedFlag && <div className="unsaved-changes">{t('teams.unsavedChanges')} <span className="cta-txt">{t('teams.unsavedChangesCta')}</span></div>}
        </>
    )
}

export default TeamReviewGeneralEdit